import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Case } from 'src/app/models/case';
import { CaseService } from '../../services/case.service';
import { MessageService } from '../../services/message.service';
import { UtilsService } from '../../services/utils.service';
import { HttpErrorResponse } from '@angular/common/http';
import { EvidenceRecordGroupByType } from '../../models/evidenceRecord';
import { FormBuilder, FormArray, FormControl } from '@angular/forms';
import { AclService } from '../../services/acl.service';
import { Approval } from '../../models/document';

interface EvidenceRecordGenerationDialogData {
  case: Case;
  allowGroupByCountry: boolean;
}

/**
 * The dialog's result when the user stops the generation to approve or reject the documents pending approval
 * first. A generated record closes the dialog with `true`.
 */
export const REVIEW_PENDING_DOCUMENTS = 'REVIEW_PENDING_DOCUMENTS';

@Component({
  selector: 'app-evidence-record-generation-dialog',
  templateUrl: './evidence-record-generation-dialog.component.html',
  styleUrls: ['./evidence-record-generation-dialog.component.scss']
})
export class EvidenceRecordGenerationDialogComponent {
  private readonly case: Case;
  private readonly allowGroupByCountry: boolean;

  public readonly groupByTypesOptions = Object.values(EvidenceRecordGroupByType);

  // OPTIONS: the generation options. PENDING_DOCUMENTS: asks whether to include the documents pending approval.
  public step: 'OPTIONS' | 'PENDING_DOCUMENTS' = 'OPTIONS';
  public pendingDocumentsCount = 0;
  public rejectedDocumentsCount = 0;
  private readonly approvalCounts: Promise<void>;

  public generationOptionsForm = this.fb.group({
    groupBy: this.fb.array([new FormControl(EvidenceRecordGroupByType.LIST)]),
    includeDocumentDataPage: this.fb.control<boolean>(true),
    annotateDocuments: this.fb.control<boolean>(true),
  });

  protected readonly annotateDocumentsOptionWhitelist = [
    'SUPERADMIN',
    'ADMIN',
    'MANAGER',
    'CASE_OPERATOR',
  ];

  // annotation option only opened to managers and up, and to case operators. Defaults to true
  get annotateDocuments(): boolean {
    if (!this.aclService.isManagerOrAboveOrCaseOperator()) return true;
    return !!this.generationOptionsForm.get('annotateDocuments')?.value;
  }

  get groupByArray(): FormArray<FormControl> {
    return this.generationOptionsForm.get('groupBy') as FormArray<FormControl>;
  }

  public addGroupBy() {
    this.groupByArray.push(new FormControl(EvidenceRecordGroupByType.LIST));
  }

  public removeGroupBy(rank: number) {
    if (this.groupByArray.length > 1) {
      this.groupByArray.removeAt(rank);
    }
  }

  public onGroupByChange(rank: number) {
    // For each level below the changed one, check validity
    for (let i = rank + 1; i < this.groupByArray.length; i++) {
      const value = this.groupByArray.at(i).value;
      if (!this.isValidOption(i, value)) {
        // Reset to default value if invalid
        this.groupByArray.at(i).setValue(EvidenceRecordGroupByType.LIST);
      }
    }
  }

  public getGroupByValidOptions(rank: number): EvidenceRecordGroupByType[] {
    const splice = this.groupByArray.value.slice(0, rank);
    // LIST ("unclassified") is part of groupByTypesOptions: leave it out here, it is always offered once, last
    return [
      ...this.groupByTypesOptions.filter(option => option !== EvidenceRecordGroupByType.LIST && !splice.includes(option)),
      EvidenceRecordGroupByType.LIST,
    ];
  }

  public isValidOption(rank: number, value: EvidenceRecordGroupByType): boolean {
    const options = this.getGroupByValidOptions(rank);
    return options.includes(value);
  }

  public canAddNewGroupBy(): boolean {
    return this.groupByArray.length < (this.groupByTypesOptions.length - 1);
  }

  private getGroupByForSend(): EvidenceRecordGroupByType[] {
    return this.groupByArray.value.filter(v => v !== EvidenceRecordGroupByType.LIST);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: EvidenceRecordGenerationDialogData,
    private readonly dialogRef: MatDialogRef<EvidenceRecordGenerationDialogComponent>,
    private readonly toastr: ToastrService,
    private readonly caseService: CaseService,
    private readonly _message: MessageService,
    private readonly utils: UtilsService,
    private readonly fb: FormBuilder,
    private readonly aclService: AclService,
  ) {
    this.case = this.data.case;
    this.allowGroupByCountry = this.data.allowGroupByCountry;
    this.groupByTypesOptions = this.groupByTypesOptions.filter(
      (type) => this.allowGroupByCountry
        || type !== EvidenceRecordGroupByType.COUNTRIES,
    );
    this.approvalCounts = this.retrieveApprovalCounts();
  }

  /**
   * Counts the case's documents pending approval and rejected. Rejected documents are never exported (the
   * backend leaves them out), pending ones are by default, unless the user declines them.
   */
  private async retrieveApprovalCounts() {
    const caseId = this.case?._id;
    if (!caseId) return;
    try {
      const [pending, rejected] = await Promise.all([Approval.PENDING, Approval.REJECTED].map((approval) =>
        this.caseService.retrieveDocuments(caseId, 1, 1, 0, undefined, undefined, undefined, undefined, undefined,
          undefined, undefined, undefined, undefined, [approval])
      ));
      this.pendingDocumentsCount = pending.totalDocs;
      this.rejectedDocumentsCount = rejected.totalDocs;
    } catch (err) {
      // the counts only drive the warnings: generating stays possible, and the backend still leaves rejected
      // documents out
      console.error(err);
    }
  }

  public onCancel() {
    this.dialogRef.close();
  }

  public async onConfirm() {
    await this.approvalCounts;
    if (this.pendingDocumentsCount > 0) {
      this.step = 'PENDING_DOCUMENTS';
      return;
    }
    void this.generateEvidenceRecord(true);
  }

  public onIncludePendingDocuments(include: boolean) {
    void this.generateEvidenceRecord(include);
  }

  public onReviewPendingDocuments() {
    this.dialogRef.close(REVIEW_PENDING_DOCUMENTS);
  }

  private async generateEvidenceRecord(includePendingDocuments: boolean) {
    try {
      if (this.case?._id) {
        this._message.emitChange('LOADING', 'START');
        await this.caseService.generateEvidenceRecord(
          this.case._id,
          this.getGroupByForSend(),
          undefined,
          !!this.generationOptionsForm.get('includeDocumentDataPage')?.value,
          this.annotateDocuments,
          includePendingDocuments,
        );
        this.handleSuccess();
      } else {
        this.toastr.error(`ERRORS.GENERIC`)
      }
    } catch (err) {
      this.handleError(err as HttpErrorResponse);
    }
  }

  private handleSuccess() {
    this.toastr.success('EVIDENCE_RECORDS.GENERATION_SUCCESS_MESSAGE', 'EVIDENCE_RECORDS.GENERATION_SUCCESS_TITLE');
    this._message.emitChange('LOADING', 'END');
    this.dialogRef.close(true);
  }

  private handleError(err: HttpErrorResponse) {
    this._message.emitChange('LOADING', 'END');
    if (err?.error?.message && this.utils.hasTranslation(`ERRORS.${err.error.message}`)) {
      this.toastr.error(`ERRORS.${err.error.message}`)
    } else {
      this.toastr.error(`ERRORS.GENERIC`)
    }
  }
}
